import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { GoogleLogo, CheckCircle, Plug, XCircle, Lock } from "@phosphor-icons/react";

/**
 * Per-user "Connect Google Calendar" card. Fetches /auth/google/status,
 * shows connect / disconnect actions, and listens for the `?google=connected`
 * query string the OAuth callback sets to auto-refresh status.
 *
 * Feature-gated by `gmail_integration` (Pro+). Free tenants see a locked upsell.
 */
export default function GoogleConnectCard({ compact = false, onChange }) {
    const { isEnabled } = useFeatures();
    const enabled = isEnabled("gmail_integration");
    const [status, setStatus] = useState(null);
    const [working, setWorking] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/google/status");
            setStatus(data);
            if (onChange) onChange(data);
        } catch {
            setStatus({ configured: false, connected: false });
        }
    }, [onChange]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Auto-refresh status when returning from OAuth flow (?google=connected)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const g = params.get("google");
        if (g) {
            refresh();
            params.delete("google");
            params.delete("reason");
            const q = params.toString();
            window.history.replaceState(
                {},
                "",
                window.location.pathname + (q ? `?${q}` : ""),
            );
        }
    }, [refresh]);

    const connect = async () => {
        setWorking(true);
        try {
            const { data } = await api.get("/auth/google/connect");
            window.location.href = data.authorization_url;
        } catch {
            setWorking(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect your Google Calendar?")) return;
        setWorking(true);
        try {
            await api.post("/auth/google/disconnect");
            await refresh();
        } finally {
            setWorking(false);
        }
    };

    if (!status) return null;

    // Feature gate — free tenants see an upsell instead of the connect card.
    if (!enabled) {
        if (compact) {
            return (
                <Link
                    to="/billing"
                    data-testid="gcal-locked-compact"
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#525252] hover:text-ink"
                >
                    <Lock size={12} weight="fill" /> Gmail integration — upgrade to unlock
                </Link>
            );
        }
        return (
            <div
                data-testid="gcal-locked-card"
                className="border border-dashed border-[#E5E5E5] bg-white p-5 sm:p-6 flex items-start gap-3"
            >
                <Lock size={20} weight="regular" className="text-[#A3A3A3] mt-0.5" />
                <div className="flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Integration</p>
                    <p className="font-heading text-lg font-bold tracking-tight">Google Calendar &amp; Meet</p>
                    <p className="text-xs text-[#525252] mt-1">
                        Auto-schedule interviews with Meet links. Available on Pro and above.
                    </p>
                </div>
                <Link
                    to="/billing"
                    data-testid="gcal-locked-upgrade"
                    className="bg-splash/20 text-ink px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-splash/40 transition-colors"
                >
                    Upgrade
                </Link>
            </div>
        );
    }

    if (!status.configured) {
        return (
            <div
                data-testid="gcal-card-unconfigured"
                className="border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3 text-xs text-[#525252]"
            >
                Google Calendar integration is not configured on the server.
            </div>
        );
    }

    if (compact) {
        return status.connected ? (
            <div
                data-testid="gcal-card-connected"
                className="inline-flex items-center gap-2 text-xs font-bold text-[#059669]"
            >
                <CheckCircle size={14} weight="fill" /> Google Calendar connected
                <button
                    type="button"
                    onClick={disconnect}
                    disabled={working}
                    className="ml-2 underline underline-offset-2 text-[#525252] hover:text-ink"
                >
                    disconnect
                </button>
            </div>
        ) : (
            <button
                type="button"
                data-testid="gcal-connect-button"
                onClick={connect}
                disabled={working}
                className="inline-flex items-center gap-2 border border-ink hover:bg-ink hover:text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors"
            >
                <GoogleLogo size={14} weight="bold" /> Connect Google Calendar
            </button>
        );
    }

    return (
        <div
            data-testid="gcal-card"
            className="border border-[#E5E5E5] bg-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-ink text-white flex items-center justify-center shrink-0">
                    <GoogleLogo size={20} weight="bold" />
                </div>
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                        Integration
                    </p>
                    <p className="font-heading text-lg font-bold tracking-tight">
                        Google Calendar &amp; Meet
                    </p>
                    {status.connected ? (
                        <p className="text-xs text-[#059669] font-bold mt-1 flex items-center gap-1">
                            <CheckCircle size={12} weight="fill" />
                            Connected as {status.google_email || "your Google account"}
                        </p>
                    ) : (
                        <p className="text-xs text-[#525252] mt-1">
                            Schedule interviews with auto-generated Meet links.
                        </p>
                    )}
                </div>
            </div>
            {status.connected ? (
                <button
                    type="button"
                    data-testid="gcal-disconnect-button"
                    onClick={disconnect}
                    disabled={working}
                    className="border border-[#E5E5E5] hover:border-ink px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-2"
                >
                    <XCircle size={14} weight="bold" /> Disconnect
                </button>
            ) : (
                <button
                    type="button"
                    data-testid="gcal-connect-button"
                    onClick={connect}
                    disabled={working}
                    className="bg-cobalt hover:bg-cobalt-dark text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                >
                    <Plug size={14} weight="bold" /> {working ? "Redirecting…" : "Connect"}
                </button>
            )}
        </div>
    );
}
